import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';
import { SearchInput } from '@/components/SearchInput';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import { cn } from 'helpers/cn';
import { FOCUS_RING, SEARCH_FIELD_CLASS } from '../../ConsolePanel';
import { exportImplementationCsv } from './export-csv';
import { useImplementationList } from '@/state/experiments/useImplementationList';

type ImplementationTopHeaderProps = {
  manager: ReturnType<typeof useImplementationList>;
  /**
   * The rows the page is showing, not everything it fetched. Experiments that finished
   * without beating the control are not listed, and exporting them anyway would put
   * work in a client's inbox that the page itself says does not exist.
   */
  experiments: ExperimentWithVerdict[];
};

export const ImplementationTopHeader = ({ manager, experiments }: ImplementationTopHeaderProps) => {
  const { implementedOnly, setImplementedOnly, searchQuery, setSearchQuery } = manager;

  return (
    <div
      className="bg-panel border-line rounded-panel flex flex-col gap-3 border px-3 py-2.5 lg:flex-row lg:items-center lg:justify-between"
      data-tour="implementation-toolbar"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        {/* SearchInput collapses to a magnifier below `md` and drops its field in
            absolutely, so it needs a positioned parent of the field's width. */}
        <div className="relative w-full md:w-auto">
          <SearchInput value={searchQuery} handleSearch={setSearchQuery} className={SEARCH_FIELD_CLASS} />
        </div>
        <div className="flex min-h-9 items-center gap-2 md:min-h-0">
          <Switch id="implemented-only" checked={!!implementedOnly} onCheckedChange={setImplementedOnly} />
          <Label htmlFor="implemented-only" className="micro-label hover:text-foreground cursor-pointer">
            Implemented Only
          </Label>
        </div>
      </div>
      <button
        type="button"
        // border-input, not border-line: the button carries no fill, so the outline is
        // the only thing drawing it. Full width on a phone so it is a real target.
        className={cn(
          'border-input text-dim hover:text-foreground hover:bg-hover flex h-9 w-full items-center justify-center gap-1.5',
          'rounded-lg border bg-transparent px-3 text-meta transition-colors duration-150 disabled:opacity-40',
          'lg:h-8 lg:w-auto lg:shrink-0 lg:justify-start',
          FOCUS_RING,
        )}
        onClick={() => exportImplementationCsv(experiments)}
        disabled={experiments.length === 0}
      >
        <Download className="size-3.5" strokeWidth={1.5} />
        Export CSV
      </button>
    </div>
  );
};
